import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  InputAdornment,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SyncAltRoundedIcon from '@mui/icons-material/SyncAltRounded';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded';
import { getPaymentLinkDeliveryResult, sendBookingPaymentLink, syncBookingPaymentLink } from '../../services/api';

/** Strip currency labels/symbols so pasted "INR 1800.00", "₹1,800", or "INR INR 1800" parses. */
function parseLocalizedAmount(value, currencyCode) {
  let s = String(value ?? '')
    .trim()
    .replace(/,/g, '')
    .replace(/\u00a0/g, ' ');
  s = s.replace(/\u20b9/g, '').replace(/[$€£]/g, '').trim();
  const cc = (currencyCode || 'INR').toUpperCase();
  const stripCodes = [...new Set([cc, 'INR', 'USD', 'EUR', 'GBP', 'AED', 'SGD'])];
  for (let i = 0; i < 6; i += 1) {
    const before = s;
    for (const c of stripCodes) {
      if (c.length !== 3) continue;
      const escaped = c.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      s = s.replace(new RegExp(`^${escaped}\\s*`, 'i'), '').trim();
    }
    if (s === before) break;
  }
  const n = parseFloat(s);
  if (!Number.isNaN(n)) return n;
  const m = s.match(/[-+]?\d+(?:\.\d*)?/);
  if (!m) return NaN;
  const n2 = parseFloat(m[0]);
  return Number.isNaN(n2) ? NaN : n2;
}

/** ISO 4217 currencies with no minor unit (amount_minor is whole units). */
const ZERO_DECIMAL_CURRENCIES = new Set([
  'BIF',
  'CLP',
  'DJF',
  'GNF',
  'JPY',
  'KMF',
  'KRW',
  'MGA',
  'PYG',
  'RWF',
  'UGX',
  'VND',
  'VUV',
  'XAF',
  'XOF',
  'XPF',
]);

function formatAmountFromMinor(minor, currencyCode) {
  const c = (currencyCode || 'INR').toUpperCase();
  const minorNum = Number(minor);
  if (!Number.isFinite(minorNum)) return '—';
  const divisor = ZERO_DECIMAL_CURRENCIES.has(c) ? 1 : 100;
  const major = minorNum / divisor;
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: c }).format(major);
  } catch {
    return `${c} ${major.toFixed(ZERO_DECIMAL_CURRENCIES.has(c) ? 0 : 2)}`;
  }
}

/**
 * Razorpay payment link actions for booking detail (email / WhatsApp / standard vs UPI).
 */
export default function BookingPaymentLinkPanel({
  bookingId,
  booking,
  remainingAmount,
  currency = 'INR',
  isDark,
  accent,
  onRefresh,
  onNotice,
  canSendEmail = true,
  canSendWhatsApp = true,
}) {
  const [linkType, setLinkType] = useState('standard');
  const [busy, setBusy] = useState(null);
  const [syncBusyId, setSyncBusyId] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingChannel, setPendingChannel] = useState(null);
  const [amountInput, setAmountInput] = useState('');
  const [amountError, setAmountError] = useState('');

  const cur = (currency || 'INR').toUpperCase();
  const canUpi = cur === 'INR';
  const guest = booking?.guest;
  const links = Array.isArray(booking?.razorpay_payment_links) ? booking.razorpay_payment_links : [];

  const pendingNum = useMemo(
    () => Math.round((Number(remainingAmount) || 0) * 100) / 100,
    [remainingAmount]
  );
  const hasBalance = pendingNum > 0.01;

  const cardSx = useMemo(
    () => ({
      position: 'relative',
      overflow: 'hidden',
      borderRadius: 2.5,
      p: 2,
      mb: 1.5,
      border: `1px solid ${isDark ? alpha('#4ce1b6', 0.35) : alpha('#0f766e', 0.28)}`,
      background: isDark
        ? `linear-gradient(145deg, ${alpha('#0f172a', 0.92)} 0%, ${alpha('#134e4a', 0.35)} 55%, ${alpha('#1e293b', 0.9)} 100%)`
        : `linear-gradient(145deg, #ffffff 0%, ${alpha('#f0fdfa', 0.95)} 45%, ${alpha('#ccfbf1', 0.55)} 100%)`,
      boxShadow: isDark
        ? `0 12px 28px ${alpha('#020617', 0.45)}, inset 0 1px 0 ${alpha('#5eead4', 0.12)}`
        : `0 10px 26px ${alpha('#0f766e', 0.12)}, inset 0 1px 0 ${alpha('#fff', 0.85)}`,
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 3,
        borderRadius: '12px 12px 0 0',
        background: `linear-gradient(90deg, ${accent}, ${alpha(accent, 0.45)}, #38bdf8)`,
        opacity: 0.95,
      },
    }),
    [accent, isDark]
  );

  const openConfirm = useCallback(
    (channel) => {
      setPendingChannel(channel);
      setAmountInput(pendingNum.toFixed(2));
      setAmountError('');
      setConfirmOpen(true);
    },
    [pendingNum]
  );

  const validateAmount = useCallback(() => {
    const v = parseLocalizedAmount(amountInput, cur);
    if (Number.isNaN(v) || v <= 0) {
      setAmountError('Enter a valid positive amount (numbers only, or e.g. 1800 or INR 1800).');
      return null;
    }
    if (v > pendingNum + 0.02) {
      setAmountError(`Cannot exceed pending balance (${pendingNum.toFixed(2)} ${cur}).`);
      return null;
    }
    setAmountError('');
    return v;
  }, [amountInput, cur, pendingNum]);

  const confirmSend = useCallback(async () => {
    const v = validateAmount();
    if (v == null || !bookingId || !pendingChannel) return;
    const lt = !canUpi && linkType === 'upi' ? 'standard' : linkType;
    setBusy(pendingChannel);
    try {
      const res = await sendBookingPaymentLink(bookingId, {
        channel: pendingChannel,
        link_type: lt,
        amount: v,
      });
      const delivery = getPaymentLinkDeliveryResult(res, pendingChannel);
      if (!delivery.ok) {
        onNotice?.(delivery.message, 'warning');
      } else {
        onNotice?.(delivery.message || 'Payment link sent.', 'success');
      }
      setConfirmOpen(false);
      setPendingChannel(null);
      if (onRefresh) await onRefresh();
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || 'Request failed';
      onNotice?.(msg, 'error');
    } finally {
      setBusy(null);
    }
  }, [
    bookingId,
    canUpi,
    linkType,
    onNotice,
    onRefresh,
    pendingChannel,
    validateAmount,
  ]);

  const syncOne = useCallback(
    async (linkId) => {
      if (!bookingId || !linkId) return;
      setSyncBusyId(linkId);
      try {
        const res = await syncBookingPaymentLink(bookingId, linkId);
        if (res?.payment_recorded) {
          onNotice?.('Payment recorded from Razorpay.', 'success');
        } else {
          onNotice?.('Status synced.', 'info');
        }
        if (onRefresh) await onRefresh();
      } catch (e) {
        const msg = e?.response?.data?.error || e?.message || 'Sync failed';
        onNotice?.(msg, 'error');
      } finally {
        setSyncBusyId(null);
      }
    },
    [bookingId, onRefresh, onNotice]
  );

  const confirmTitle =
    pendingChannel === 'email'
      ? 'Send payment link by email'
      : pendingChannel === 'whatsapp'
        ? 'Send payment link by WhatsApp'
        : pendingChannel === 'both'
          ? 'Send payment link (email + WhatsApp)'
          : 'Confirm payment link';

  if (!bookingId || !hasBalance) {
    return null;
  }

  return (
    <Box sx={cardSx}>
      <Stack direction="row" alignItems="flex-start" spacing={1.25} sx={{ mb: 1.5, pt: 0.5 }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2,
            display: 'grid',
            placeItems: 'center',
            background: isDark ? alpha(accent, 0.2) : alpha(accent, 0.12),
            border: `1px solid ${alpha(accent, 0.45)}`,
            flexShrink: 0,
          }}
        >
          <PaymentsRoundedIcon sx={{ color: accent, fontSize: 26 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: isDark ? '#e2e8f0' : '#0f172a', letterSpacing: 0.2 }}>
            Collect balance
          </Typography>
          <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', display: 'block', mt: 0.35, lineHeight: 1.45 }}>
            Secure Razorpay link — confirm amount before send. Keys stay on the server; guests pay on Razorpay.
          </Typography>
        </Box>
      </Stack>

      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1.25, flexWrap: 'wrap', gap: 0.75 }}>
        <SecurityRoundedIcon sx={{ fontSize: 18, color: isDark ? '#94a3b8' : '#64748b' }} />
        <Typography variant="caption" sx={{ fontWeight: 700, color: isDark ? '#cbd5e1' : '#475569' }}>
          Link type
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={canUpi ? linkType : 'standard'}
          onChange={(_, v) => v && setLinkType(v)}
          sx={{
            '& .MuiToggleButton-root': {
              px: 1.25,
              py: 0.35,
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.72rem',
              borderColor: isDark ? alpha('#5eead4', 0.35) : alpha('#0f766e', 0.25),
            },
            '& .Mui-selected': {
              bgcolor: `${alpha(accent, isDark ? 0.28 : 0.18)} !important`,
              color: isDark ? '#ecfdf5' : '#0f172a',
            },
          }}
        >
          <ToggleButton value="standard">All methods</ToggleButton>
          <ToggleButton value="upi" disabled={!canUpi}>
            UPI-first
          </ToggleButton>
        </ToggleButtonGroup>
        {!canUpi && (
          <Chip size="small" label="UPI links: INR only" sx={{ height: 22, fontSize: '0.65rem', fontWeight: 600 }} />
        )}
      </Stack>

      {!guest && (
        <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 600, display: 'block', mb: 1 }}>
          Add a primary guest (with email / phone) to send a payment link.
        </Typography>
      )}

      {!canSendEmail && !canSendWhatsApp && (
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
          Payment link messaging is disabled in Configuration → Config.
        </Typography>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.1} sx={{ mb: 1.25 }}>
        {canSendEmail && (
        <Tooltip title="Email Razorpay checkout link">
          <span>
            <Button
              fullWidth
              variant="contained"
              disabled={!guest || busy}
              onClick={() => openConfirm('email')}
              startIcon={busy === 'email' ? <CircularProgress size={18} color="inherit" /> : <EmailRoundedIcon />}
              sx={{
                py: 1.1,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 800,
                background: `linear-gradient(135deg, #0d9488 0%, ${accent} 100%)`,
                boxShadow: `0 8px 20px ${alpha(accent, 0.35)}`,
                '&:hover': { background: `linear-gradient(135deg, #0f766e 0%, #0d9488 100%)` },
              }}
            >
              Email link
            </Button>
          </span>
        </Tooltip>
        )}
        {canSendWhatsApp && (
        <Tooltip title="WhatsApp message with secure link">
          <span>
            <Button
              fullWidth
              variant="outlined"
              disabled={!guest || busy}
              onClick={() => openConfirm('whatsapp')}
              startIcon={busy === 'whatsapp' ? <CircularProgress size={18} /> : <WhatsAppIcon />}
              sx={{
                py: 1.1,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 800,
                borderWidth: 2,
                borderColor: alpha('#25D366', 0.75),
                color: isDark ? '#bbf7d0' : '#047857',
                backgroundColor: isDark ? alpha('#14532d', 0.35) : alpha('#dcfce7', 0.6),
                '&:hover': {
                  borderWidth: 2,
                  borderColor: '#22c55e',
                  backgroundColor: isDark ? alpha('#14532d', 0.55) : alpha('#bbf7d0', 0.85),
                },
              }}
            >
              WhatsApp link
            </Button>
          </span>
        </Tooltip>
        )}
        {canSendEmail && canSendWhatsApp && (
        <Tooltip title="Send both channels">
          <span>
            <Button
              fullWidth
              variant="outlined"
              disabled={!guest || busy}
              onClick={() => openConfirm('both')}
              startIcon={busy === 'both' ? <CircularProgress size={18} /> : <LinkRoundedIcon />}
              sx={{
                py: 1.1,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 800,
                borderColor: alpha(accent, 0.55),
                color: accent,
              }}
            >
              Both
            </Button>
          </span>
        </Tooltip>
        )}
      </Stack>

      <Dialog
        open={confirmOpen}
        onClose={() => !busy && setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            border: `1px solid ${isDark ? alpha('#4ce1b6', 0.35) : alpha('#0f766e', 0.22)}`,
            background: isDark
              ? `linear-gradient(180deg, ${alpha('#0f172a', 0.98)} 0%, ${alpha('#1e293b', 0.98)} 100%)`
              : '#fff',
            boxShadow: isDark ? `0 24px 48px ${alpha('#000', 0.5)}` : `0 20px 40px ${alpha('#0f766e', 0.15)}`,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, pt: 2.5, pb: 1, color: isDark ? '#f1f5f9' : '#0f172a' }}>
          {confirmTitle}
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <Typography variant="body2" sx={{ color: isDark ? '#94a3b8' : '#64748b', mb: 2 }}>
            Pending balance:{' '}
            <Box component="span" sx={{ fontWeight: 800, color: isDark ? '#99f6e4' : '#0f766e' }}>
              {cur} {pendingNum.toFixed(2)}
            </Box>
            . You may charge less for a partial payment; you cannot exceed this amount.
          </Typography>
          <TextField
            fullWidth
            label="Amount to collect"
            value={amountInput}
            onChange={(e) => {
              setAmountInput(e.target.value);
              setAmountError('');
            }}
            onBlur={() => {
              const v = parseLocalizedAmount(amountInput, cur);
              if (!Number.isNaN(v) && v > 0) {
                setAmountInput(v.toFixed(2));
              }
            }}
            error={Boolean(amountError)}
            helperText={amountError || `Enter numbers only here — ${cur} is shown on the left (same as Razorpay).`}
            InputProps={{
              startAdornment: <InputAdornment position="start">{cur}</InputAdornment>,
            }}
            inputProps={{ inputMode: 'decimal' }}
            disabled={Boolean(busy)}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                fontWeight: 700,
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, gap: 1 }}>
          <Button onClick={() => setConfirmOpen(false)} disabled={Boolean(busy)} sx={{ textTransform: 'none', fontWeight: 700 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmSend}
            disabled={Boolean(busy)}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              px: 2.5,
              borderRadius: 2,
              background: `linear-gradient(135deg, #0d9488 0%, ${accent} 100%)`,
            }}
            startIcon={busy ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {busy ? 'Sending…' : 'Confirm & send'}
          </Button>
        </DialogActions>
      </Dialog>

      {links.length > 0 && (
        <>
          <Divider sx={{ my: 1.25, borderColor: isDark ? alpha('#64748b', 0.35) : alpha('#94a3b8', 0.35) }} />
          <Typography variant="caption" sx={{ fontWeight: 800, color: isDark ? '#94a3b8' : '#64748b', letterSpacing: 0.6, textTransform: 'uppercase' }}>
            Recent links
          </Typography>
          <Stack spacing={0.75} sx={{ mt: 0.75 }}>
            {links.map((row) => (
              <Stack
                key={row.id}
                direction="row"
                alignItems="flex-start"
                spacing={1}
                sx={{
                  py: 0.75,
                  px: 1,
                  borderRadius: 1.5,
                  bgcolor: isDark ? alpha('#0f172a', 0.45) : alpha('#f8fafc', 0.95),
                  border: `1px solid ${isDark ? alpha('#334155', 0.9) : alpha('#e2e8f0', 1)}`,
                }}
              >
                <Chip
                  size="small"
                  label={row.status || '—'}
                  sx={{ fontWeight: 700, textTransform: 'capitalize', minWidth: 88, mt: 0.25 }}
                  color={row.status === 'paid' ? 'success' : row.status === 'expired' ? 'default' : 'primary'}
                  variant={row.status === 'paid' ? 'filled' : 'outlined'}
                />
                <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 800, color: isDark ? '#99f6e4' : '#0f766e', letterSpacing: '-0.01em' }}
                  >
                    {formatAmountFromMinor(row.amount_minor, row.currency || cur)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: isDark ? '#cbd5e1' : '#334155', fontFamily: 'ui-monospace, monospace' }} noWrap>
                    {row.reference_id}
                  </Typography>
                </Stack>
                {row.status !== 'paid' && (
                  <Tooltip title="Sync status from Razorpay">
                    <Button
                      size="small"
                      onClick={() => syncOne(row.id)}
                      disabled={syncBusyId === row.id}
                      sx={{ minWidth: 0, px: 1, mt: 0.25 }}
                    >
                      {syncBusyId === row.id ? <CircularProgress size={18} /> : <SyncAltRoundedIcon fontSize="small" />}
                    </Button>
                  </Tooltip>
                )}
              </Stack>
            ))}
          </Stack>
        </>
      )}
    </Box>
  );
}
